import { useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';

import type { UserRole } from '../model/types';

/** Coerce `/users/roles` payloads into a role array (guards non-array API/error shapes). */
export function normalizeUserRolesPayload(payload: unknown): Array<UserRole> {
  if (Array.isArray(payload)) {
    return payload as Array<UserRole>;
  }
  if (payload != null && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    if (Array.isArray(record.data)) {
      return record.data as Array<UserRole>;
    }
    if (Array.isArray(record.roles)) {
      return record.roles as Array<UserRole>;
    }
  }
  return [];
}

export const getUserRolesAPI = async () => {
  const response = await baseAPI.get<unknown>(`/users/roles`);
  return normalizeUserRolesPayload(response.data);
};

// Shared query options to ensure consistency between hook and imperative cache access
export const getUserRolesQueryOptions = () => ({
  queryKey: QUERY_KEYS.USER_ROLES(),
  queryFn: () => getUserRolesAPI(),
  staleTime: 1000 * 60 * 5, // Data is considered fresh for 5 minutes
  gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes even when unused
  refetchOnWindowFocus: false, // Don't refetch on window focus
});

export const useGetUserRoles = () => {
  return useQuery(getUserRolesQueryOptions());
};

